/**
 * Poseidon dashboard — sortable data table + CSV export.
 *
 * Sorting is client-side over the rows already returned, and it is stable, so a
 * sort can never change which data window the operator is looking at. Missing
 * values always sort last: an empty cell must not masquerade as the smallest.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { downloadCsv, toCsv } from '../lib/format';
import type { CsvColumn } from '../lib/format';
import { cx } from '../lib/cx';
import type { Tone } from '../lib/staleness';
import { useSortableTable, useSortedRows } from '../hooks/useSortableTable';
import type { SortDirection, SortState } from '../hooks/useSortableTable';
import { EmptyState } from './primitives';

type Comparable = string | number | boolean | null | undefined;

export interface TableColumn<TRow, TKey extends string> {
  key: TKey;
  header: string;
  render: (row: TRow) => ReactNode;
  /** Enables sorting for this column and defines the compared value. */
  sortValue?: (row: TRow) => Comparable;
  align?: 'left' | 'right' | 'center';
  width?: string;
  title?: string;
}

export interface DataTableProps<TRow, TKey extends string> {
  rows: readonly TRow[];
  columns: readonly TableColumn<TRow, TKey>[];
  rowKey: (row: TRow) => string;
  initialSort?: SortState<TKey>;
  onRowClick?: (row: TRow) => void;
  emptyTitle?: string;
  emptyMessage?: string;
  caption?: string;
  /** Row-level tone, e.g. breach rows in the risk table. */
  rowTone?: (row: TRow) => Tone | null;
  footnote?: ReactNode;
}

export function DataTable<TRow, TKey extends string>({
  rows,
  columns,
  rowKey,
  initialSort,
  onRowClick,
  emptyTitle = 'Nothing to show',
  emptyMessage,
  caption,
  rowTone,
  footnote,
}: DataTableProps<TRow, TKey>): JSX.Element {
  const { sort, toggle, ariaSort, indicator } = useSortableTable<TKey>(initialSort ?? { key: null, direction: 'desc' });

  const accessors = useMemo(() => {
    const map: Partial<Record<TKey, (row: TRow) => Comparable>> = {};
    for (const column of columns) {
      if (column.sortValue !== undefined) map[column.key] = column.sortValue;
    }
    return map;
  }, [columns]);

  const sorted = useSortedRows<TRow, TKey>(rows, sort, accessors);

  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} message={emptyMessage} />;
  }

  return (
    <>
      <div className="table-wrap">
        <table className="data-table">
          {caption !== undefined && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((column) => {
                const sortable = column.sortValue !== undefined;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={cx(sortable && 'sortable')}
                    style={{ textAlign: column.align ?? 'left', width: column.width }}
                    aria-sort={sortable ? ariaSort(column.key) : undefined}
                    title={column.title}
                    onClick={sortable ? () => toggle(column.key) : undefined}
                  >
                    {column.header}
                    {sortable && <span aria-hidden="true"> {indicator(column.key)}</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const tone = rowTone?.(row) ?? null;
              return (
                <tr
                  key={rowKey(row)}
                  className={cx(onRowClick !== undefined && 'clickable', tone === 'warn' && 'row-warn', tone === 'danger' && 'row-danger')}
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                  tabIndex={onRowClick === undefined ? undefined : 0}
                  onKeyDown={
                    onRowClick === undefined
                      ? undefined
                      : (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick(row);
                          }
                        }
                  }
                >
                  {columns.map((column) => (
                    <td key={column.key} style={{ textAlign: column.align ?? 'left' }}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footnote !== undefined && <div className="text-faint" style={{ padding: '8px 0 0' }}>{footnote}</div>}
    </>
  );
}

/**
 * §3.2 B requires CSV export. This exports what is on screen; the daemon also
 * exposes `/trades/closed/export.csv` for windows too large to hold in memory.
 */
export function CsvExportButton<TRow>({
  rows,
  columns,
  filename,
  label = 'Export CSV',
  disabled = false,
}: {
  rows: readonly TRow[];
  columns: readonly CsvColumn<TRow>[];
  filename: string;
  label?: string;
  disabled?: boolean;
}): JSX.Element {
  const exportNow = (): void => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    downloadCsv(filename.replace('.csv', `-${stamp}.csv`), toCsv(rows, columns));
  };

  return (
    <button type="button" className="btn btn--sm" onClick={exportNow} disabled={disabled || rows.length === 0} title="Export the rows currently loaded in this table">
      {label} ({rows.length})
    </button>
  );
}

export type { SortDirection, SortState };
